import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Profile, SortDirection } from '../../../../global-state/types';
import { useUser } from '../../../../hooks';
import { Icon, Icons, Tooltip, Typography } from '../../../../stories/atoms';
import { Button } from '../../../../stories/molecules';
import { Table, HeaderRowItem, RowItem } from '../../../../stories/molecules/tablev2/Table';
import {
  commonTranslations,
  employeesTranslations,
  permissionsTranslations,
  profileTranslations,
} from '../../../../translations';
import { RoleMapping, SuamRole } from '../../../../utils';
import Copy from '../../../../stories/molecules/copy/Copy';
import { RowFlexContainer } from '../../../../stories/atoms/containerV2/ContainerV2';
import { StyledEmployeeTable, StyledProfileImage } from './EmployeeTable.styles';

enum ColumnId {
  Name = 'name',
  Upn = 'upn',
  OtherRoles = 'otherRoles',
  Permissions = 'permissions',
}

export type ButtonType = 'permissions';
export type RoleData = { key: SuamRole | 'other'; employees: Profile[] };

const {
  employees: { view, otherRoles, other, otherToolTip },
} = employeesTranslations;
const { fullName, name, upn: emailTranslation } = profileTranslations;
const { yes, no, you } = commonTranslations;

export const EmployeeTable: FC<
  React.PropsWithChildren<{
    tableData: RoleData;
    handleItemClick: (employee: Profile, buttonType: ButtonType) => void;
  }>
> = ({ tableData, handleItemClick }) => {
  const { t } = useTranslation();
  const {
    get: { profile, currentStoreId, userPermissions },
  } = useUser();

  const [sortColumnId, setSortColumnId] = useState<ColumnId | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);

  const canReadPermissions = userPermissions.employeePermissions.canRead;

  const headerItems: HeaderRowItem<ColumnId>[] = [
    {
      id: ColumnId.Name,
      content: <Typography>{t(name.key, name.defaultValue)}</Typography>,
      width: '30%',
    },
    {
      id: ColumnId.Upn,
      content: <Typography>{t(emailTranslation.key, emailTranslation.defaultValue)}</Typography>,
      width: '30%',
    },
    {
      id: ColumnId.OtherRoles,
      content: <Typography>{t(otherRoles.key, otherRoles.defaultValue)}</Typography>,
      width: '20%',
    },
    {
      id: ColumnId.Permissions,
      content: (
        <Typography>{t(permissionsTranslations.title.key, permissionsTranslations.title.defaultValue)}</Typography>
      ),
      width: '20%',
    },
  ];

  const sortConfig = {
    name: {
      getItemValue: (employee: Profile) => employee.firstName,
    },
    upn: {
      getItemValue: (employee: Profile) => employee.upn.toLocaleLowerCase(),
    },
    otherRoles: {
      getItemValue: (employee: Profile) => getAssignmentLength(employee),
    },
  };

  const getAssignmentLength = (employee: Profile) => {
    return (
      employee.assignments
        ?.filter(({ locationCode }) => locationCode === currentStoreId)
        .map(({ suamRoleCode }) => suamRoleCode) || []
    ).length;
  };

  const getRowItems = (employee: Profile): RowItem<ColumnId>[] => {
    const { firstName, lastName, upn, id, avatar } = employee;
    const avatarUrl = avatar?.url;

    return [
      {
        id: ColumnId.Name,
        content: (
          <RowFlexContainer alignItems="center" gap={[3]}>
            {avatarUrl ? (
              <StyledProfileImage src={`${avatarUrl}${process.env.REACT_APP_CDN_TOKEN}`} alt="profile-avatar" />
            ) : (
              <Icon data-testid="default-profile-icon" icon={Icons.PROFILE} size={'medium'} />
            )}
            {id === profile.id
              ? `${t(fullName.key, { defaultValue: fullName.defaultValue, firstName, lastName })} 
                (${t(you.key, you.defaultValue)})`
              : t(fullName.key, { defaultValue: fullName.defaultValue, firstName, lastName })}
          </RowFlexContainer>
        ),
      },
      {
        id: ColumnId.Upn,
        content: (
          <RowFlexContainer gap={[1]}>
            {upn}
            <Copy textToCopy={upn} />
          </RowFlexContainer>
        ),
      },
      {
        id: ColumnId.OtherRoles,
        content: getAssignmentLength(employee) > 1 ? t(yes.key, yes.defaultValue) : t(no.key, no.defaultValue),
      },
      {
        id: ColumnId.Permissions,
        content:
          id === profile.id || !canReadPermissions ? (
            <span></span>
          ) : (
            <Button
              label={t(view.key, view.defaultValue)}
              onClick={() => handleItemClick(employee, 'permissions')}
              variant="text"
              buttonContentProps={{ textOptions: { wrapText: true } }}
            />
          ),
      },
    ];
  };

  const onColumnSort = (columnId: ColumnId) => {
    if (columnId === sortColumnId) {
      setSortDirection(
        sortDirection === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending,
      );
    } else {
      setSortColumnId(columnId);
      setSortDirection(SortDirection.Ascending);
    }
  };

  return (
    <StyledEmployeeTable data-testid="employee-table">
      <RowFlexContainer alignItems="center" padding={[0, 0, 4, 0]}>
        <Typography type="h4">
          {tableData.key === 'other'
            ? t(other.key, other.defaultValue)
            : t(`employees.roles.${RoleMapping[tableData.key]}`, RoleMapping[tableData.key])}
        </Typography>
        <Tooltip
          text={
            <Typography padding={[2]} color="white">
              {tableData.key === 'other'
                ? t(otherToolTip.key, otherToolTip.defaultValue)
                : t(`employees.roles.${RoleMapping[tableData.key]}`, RoleMapping[tableData.key])}
            </Typography>
          }
          position="bottom-start"
        >
          <Icon data-testid="infoIcon" icon={Icons.INFO} margin={[0, 2]} />
        </Tooltip>
      </RowFlexContainer>

      <Table
        headerItems={headerItems}
        items={tableData.employees}
        getRowItems={getRowItems}
        useZebraStyles={true}
        cellSize="small"
        sortConfig={sortConfig}
        sortColumnId={sortColumnId || undefined}
        sortDirection={sortDirection || undefined}
        onSort={onColumnSort}
      />
    </StyledEmployeeTable>
  );
};
